import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User, Menu, X, Book, User as UserIcon, Map, LogOut, Users, ChevronDown, Crown, Play, FileText, DollarSign, MessageCircle, PenTool, Sun, Moon, Grid, Palette, MoreHorizontal, Trophy, Heart, Shield, Church, ListOrdered, Smile, BookUser, Scale, Gamepad2, HandHeart, BookOpen } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ThemeToggle from '@/components/ThemeToggle';
import AdminIndicator from '@/components/AdminIndicator';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { useResponsive } from '@/hooks/useResponsive';
import { supabase } from '@/integrations/supabase/client';
import LanguageToggle from "./LanguageToggle";
import TouchOptimizedButton from '@/components/ui/touch-optimized-button';
import SmoothTransition from '@/components/ui/smooth-transition';
import SwipeGesture from '@/components/ui/swipe-gesture';
import { cn } from '@/lib/utils';
import "@/i18n/config";
import { useTranslation } from "react-i18next";
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { userRole, isAdmin } = useSubscriptionContext();
  const {
    isMobile,
    touchDevice,
    prefersReducedMotion
  } = useResponsive();
  const {
    t
  } = useTranslation();

  // Handle scroll for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };
  const dropdownItems = [{
    path: '/prayers',
    label: 'navigation.prayers',
    icon: HandHeart
  }, {
    path: '/saint-pius-x',
    label: 'navigation.saintPiusX',
    icon: UserIcon
  }, {
    path: '/catechism',
    label: 'navigation.catechism',
    icon: Book
  }, {
    path: '/blog',
    label: 'navigation.blog',
    icon: PenTool,
    requiresParent: true
  }, {
    path: '/ask-saint-pius-x',
    label: 'navigation.askSaintPiusX',
    icon: MessageCircle
  }, {
    path: '/saint-of-the-day',
    label: 'navigation.saintOfTheDay',
    icon: Crown
  }, {
    path: '/about-us',
    label: 'navigation.aboutUs',
    icon: Users
  }, {
    path: '/pricing',
    label: 'navigation.subscription',
    icon: DollarSign
  }, {
    path: '/donate',
    label: 'navigation.donate',
    icon: Heart
  }, {
    path: '/demo',
    label: 'navigation.demo',
    icon: Play
  }];
  const printableItems = [{
    path: '/word-search',
    label: 'navigation.wordSearch',
    icon: FileText
  }, {
    path: '/crossword',
    label: 'navigation.crossword',
    icon: Grid
  }, {
    path: '/coloring-page',
    label: 'navigation.coloringPage',
    icon: Palette
  }, {
    path: '/liturgical-objects',
    label: 'navigation.liturgicalObjects',
    icon: Church
  }, {
    path: '/sacramentals-memory',
    label: 'navigation.sacramentalsMemory',
    icon: Shield
  }, {
    path: '/sequence-builder',
    label: 'navigation.sequenceBuilder',
    icon: ListOrdered
  }, {
    path: '/emoji-quiz',
    label: 'navigation.emojiQuiz',
    icon: Smile
  }, {
    path: '/virtue-vice',
    label: 'navigation.virtueVice',
    icon: Scale
  }, {
    path: '/rosary-art',
    label: 'navigation.rosaryArt',
    icon: BookUser
  }, {
    path: '/daily-trivia',
    label: 'navigation.catholicTriviaGame',
    icon: Trophy
  }, {
    path: '/guess-the-saint',
    label: 'navigation.guessTheSaint',
    icon: BookUser
  }];
  const handleSwipeDown = () => {
    if (isMobile && !isOpen) {
      setIsOpen(true);
    }
  };
  const handleSwipeUp = () => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  };
  return <nav className={cn('bg-card/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 transition-all duration-300', isScrolled ? 'shadow-lg bg-card/98' : 'shadow-sm',
  // Ensure proper touch targets on mobile
  'min-h-[56px]')}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className={cn('flex items-center space-x-2 transition-transform duration-200',
        // Enhanced touch target for mobile
        isMobile && 'min-h-[44px] py-2',
        // Hover effect for non-touch devices
        !touchDevice && 'hover:scale-105')}>
              <img src="/assets/mappa-caeli-logo.png" alt="Mappa Caeli Logo" className="w-10 h-10 object-contain drop-shadow-sm" />
              
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Pages Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size={touchDevice ? "lg" : "default"} 
                    className="flex items-center space-x-1 font-quest text-sm"
                    aria-label="More pages"
                  >
                    <MoreHorizontal size={16} />
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-popover border border-border shadow-lg z-50">
                  {dropdownItems
                    .filter(item => {
                      // Show "Ask Saint Pius X" only for logged-in users
                      if (item.path === '/ask-saint-pius-x') {
                        return session !== null;
                      }
                      // Show Blog only for logged-in parents
                      if (item.requiresParent) {
                        return session !== null && userRole === 'parent';
                      }
                      return true;
                    })
                    .map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return <DropdownMenuItem key={item.path} asChild>
                        <Link to={item.path} className={cn('flex items-center space-x-2 px-3 py-3 cursor-pointer transition-all duration-200',
                  // Enhanced touch targets
                  'min-h-[44px]', isActive ? 'bg-primary text-primary-foreground font-semibold' : 'text-foreground hover:bg-muted hover:text-foreground')}>
                          <Icon size={16} />
                          <span>{t(item.label)}</span>
                        </Link>
                      </DropdownMenuItem>;
              })}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Pilgrim's Arcade Link - Highlighted */}
              <Link 
                to="/pilgrims-arcade" 
                className={cn(
                  'flex items-center space-x-1 px-3 py-2 rounded-lg font-quest transition-all duration-200 text-sm',
                  'bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold',
                  'shadow-[0_0_12px_rgba(251,191,36,0.5)] hover:shadow-[0_0_20px_rgba(251,191,36,0.7)]',
                  'hover:from-amber-400 hover:to-orange-400',
                  touchDevice && 'min-h-[44px]'
                )}
              >
                <Gamepad2 size={16} />
                <span>{t('navigation.pilgrimsArcade')}</span>
              </Link>
              
              
              {/* Quest Map */}
              <Link to="/quest-map" className={cn('flex items-center space-x-1 px-3 py-2 rounded-lg font-quest transition-all duration-200 text-sm', touchDevice && 'min-h-[44px]', location.pathname === '/quest-map' ? 'bg-primary text-primary-foreground font-semibold shadow-md' : 'text-foreground hover:bg-muted hover:text-foreground')}>
                <Map size={16} />
                <span>{t('navigation.questMap')}</span>
              </Link>

              {/* Bible Study */}
              <Link to="/bible-study" className={cn('flex items-center space-x-1 px-3 py-2 rounded-lg font-quest transition-all duration-200 text-sm', touchDevice && 'min-h-[44px]', location.pathname.startsWith('/bible-study') ? 'bg-primary text-primary-foreground font-semibold shadow-md' : 'text-foreground hover:bg-muted hover:text-foreground')}>
                <Book size={16} />
                <span>{t('navigation.bibleStudy')}</span>
              </Link>

              {/* Mappa Caeli Learning */}
              <Link to="/mappa-caeli-learning" className={cn('flex items-center space-x-1 px-3 py-2 rounded-lg font-quest transition-all duration-200 text-sm', touchDevice && 'min-h-[44px]', location.pathname.startsWith('/mappa-caeli-learning') ? 'bg-primary text-primary-foreground font-semibold shadow-md' : 'text-foreground hover:bg-muted hover:text-foreground')}>
                <BookOpen size={16} />
                <span>{t('navigation.mappaCaeliLearning')}</span>
              </Link>



              {/* Admin Dashboard */}
              {isAdmin && (
                <Link to="/admin" className={cn('flex items-center space-x-1 px-3 py-2 rounded-lg font-quest transition-all duration-200 text-sm', touchDevice && 'min-h-[44px]', location.pathname.startsWith('/admin') ? 'bg-primary text-primary-foreground font-semibold shadow-md' : 'text-foreground hover:bg-muted hover:text-foreground')}>
                  <Shield size={16} />
                  <span>Admin</span>
                </Link>
              )}
              
              <div className="flex items-center space-x-2 ml-4">
                <AdminIndicator />
                <ThemeToggle />
                <LanguageToggle />
                {session ? <>
                    <TouchOptimizedButton asChild variant="outline" size={touchDevice ? "touch" : "sm"} className="text-sm">
                      <Link to="/dashboard">{t("navigation.dashboard")}</Link>
                    </TouchOptimizedButton>
                    <TouchOptimizedButton onClick={handleLogout} variant="ghost" size={touchDevice ? "touch" : "sm"} className="text-foreground hover:text-muted-foreground">
                      <LogOut size={20} />
                      <span className="sr-only">{t("navigation.logout")}</span>
                    </TouchOptimizedButton>
                  </> : <>
                    <TouchOptimizedButton asChild variant="outline" size={touchDevice ? "touch" : "sm"} className="text-sm">
                      <Link to="/login">{t("navigation.login")}</Link>
                    </TouchOptimizedButton>
                    <TouchOptimizedButton asChild variant="primary" size={touchDevice ? "touch" : "sm"} className="text-sm">
                      <Link to="/signup">{t("navigation.signup")}</Link>
                    </TouchOptimizedButton>
                  </>}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <LanguageToggle />
              <TouchOptimizedButton onClick={() => setIsOpen(!isOpen)} variant="ghost" size="touch" className="text-foreground hover:text-muted-foreground" touchFeedback={!prefersReducedMotion} aria-label={isOpen ? "Close menu" : "Open menu"}>
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </TouchOptimizedButton>
            </div>
          </div>

          {/* Mobile Navigation */}
          <SmoothTransition show={isOpen && isMobile} type="slide-down" duration={prefersReducedMotion ? 0 : 300}>
            <div className="md:hidden bg-gradient-to-b from-card to-card/95 backdrop-blur-sm border-t border-border shadow-xl max-h-[calc(100vh-140px)] overflow-y-auto">
              {/* Enhanced mobile menu with better visual hierarchy */}
              <div className="px-3 pt-3 pb-4 space-y-1">{/* Reduced spacing from space-y-2 to space-y-1 and reduced padding */}
                
                {/* Primary Navigation Section */}
                <div className="space-y-1">
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-medieval font-semibold text-muted-foreground uppercase tracking-wide">
                      Navigation
                    </h3>
                  </div>
                  
                  {/* Blog - Standalone link */}
                  {session && userRole === 'parent' && (
                    <Link to="/blog" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
                // Reduced from 52px to 44px
                location.pathname === '/blog' ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                      <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                  // Reduced from w-8 h-8 to w-6 h-6
                  location.pathname === '/blog' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                        <PenTool size={16} /> {/* Reduced from 18 to 16 */}
                      </div>
                      <span className="text-sm font-medium">{t('navigation.blog')}</span> {/* Reduced from text-base to text-sm */}
                      {location.pathname === '/blog' && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                    </Link>
                  )}
                  
                  {/* Quest Map */}
                  <Link to="/quest-map" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
              location.pathname === '/quest-map' ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                    <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                location.pathname === '/quest-map' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                      <Map size={16} />
                    </div>
                    <span className="text-sm font-medium">{t('navigation.questMap')}</span>
                    {location.pathname === '/quest-map' && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                  </Link>

                  {/* Bible Study */}
                  <Link to="/bible-study" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
              location.pathname.startsWith('/bible-study') ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                    <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                location.pathname.startsWith('/bible-study') ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                      <Book size={16} />
                    </div>
                    <span className="text-sm font-medium">{t('navigation.bibleStudy')}</span>
                    {location.pathname.startsWith('/bible-study') && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                  </Link>

                  {/* Mappa Caeli Learning */}
                  <Link to="/mappa-caeli-learning" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
              location.pathname.startsWith('/mappa-caeli-learning') ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                    <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                location.pathname.startsWith('/mappa-caeli-learning') ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                      <BookOpen size={16} />
                    </div>
                    <span className="text-sm font-medium">{t('navigation.mappaCaeliLearning')}</span>
                    {location.pathname.startsWith('/mappa-caeli-learning') && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                  </Link>

                  {/* Saint of the Day - Only for logged-in children */}
                  {session && userRole === 'child' && (
                    <Link to="/saint-of-the-day" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
                  location.pathname === '/saint-of-the-day' ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                      <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                    location.pathname === '/saint-of-the-day' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                        <Crown size={16} />
                      </div>
                      <span className="text-sm font-medium">{t('navigation.saintOfTheDay')}</span>
                      {location.pathname === '/saint-of-the-day' && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                    </Link>
                  )}


                  {/* Admin Dashboard */}
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
                  location.pathname.startsWith('/admin') ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')}>
                      <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                    location.pathname.startsWith('/admin') ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                        <Shield size={16} />
                      </div>
                      <span className="text-sm font-medium">Admin</span>
                      {location.pathname.startsWith('/admin') && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                    </Link>
                  )}
                </div>

                {/* Divider */}
                <div className="px-3">
                  <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                </div>

                {/* Learn More Section */}
                <div className="space-y-1">
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-medieval font-semibold text-muted-foreground uppercase tracking-wide">
                      {t('navigation.learnMore')}
                    </h3>
                  </div>
                  
                  {/* Other pages */}
                  {dropdownItems
                    .filter(item => {
                      // Show "Ask Saint Pius X" only for logged-in users
                      if (item.path === '/ask-saint-pius-x') {
                        return session !== null;
                      }
                      return true;
                    })
                    .map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)} className={cn('flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 'min-h-[44px] group relative overflow-hidden',
                // Reduced from 52px to 44px
                isActive ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]')} style={{
                  animationDelay: `${index * 50}ms`
                }}>
                        <div className={cn('flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                  // Reduced from w-8 h-8 to w-6 h-6
                  isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary')}>
                          <Icon size={16} /> {/* Reduced from 18 to 16 */}
                        </div>
                        <span className="text-sm font-medium">{t(item.label)}</span> {/* Reduced from text-base to text-sm */}
                        {isActive && <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />}
                      </Link>;
              })}
                </div>

                {/* Divider */}
                <div className="px-3">
                  <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                </div>

                {/* Pilgrim's Arcade Link */}
                <Link 
                  to="/pilgrims-arcade" 
                  onClick={() => setIsOpen(false)} 
                  className={cn(
                    'flex items-center space-x-3 px-4 py-2 rounded-xl font-quest transition-all duration-300', 
                    'min-h-[44px] group relative overflow-hidden mx-3',
                    location.pathname === '/pilgrims-arcade' 
                      ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25' 
                      : 'text-foreground hover:bg-muted/80 active:bg-muted hover:shadow-md active:scale-[0.98]'
                  )}
                >
                  <div className={cn(
                    'flex items-center justify-center w-6 h-6 rounded-lg transition-all duration-300',
                    location.pathname === '/pilgrims-arcade' 
                      ? 'bg-primary-foreground/20 text-primary-foreground' 
                      : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                  )}>
                    <Gamepad2 size={16} />
                  </div>
                  <span className="text-sm font-medium">{t('navigation.pilgrimsArcade')}</span>
                  {location.pathname === '/pilgrims-arcade' && (
                    <div className="ml-auto w-2 h-2 bg-primary-foreground/60 rounded-full animate-pulse" />
                  )}
                </Link>

                {/* Divider */}
                <div className="px-3">
                  <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                </div>

                {/* Settings Section */}
                <div className="space-y-3">
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-medieval font-semibold text-muted-foreground uppercase tracking-wide">
                      Settings
                    </h3>
                  </div>

                  {/* Theme Toggle in Mobile Menu */}
                  <button onClick={() => {
                // We'll let the ThemeToggle handle the theme change
                const themeToggle = document.querySelector('[data-theme-toggle]') as HTMLElement;
                if (themeToggle) themeToggle.click();
              }} className="flex items-center justify-between w-full px-4 py-3 rounded-xl bg-muted/40 border border-border/50 hover:bg-muted/60 active:bg-muted/70 transition-all duration-300 active:scale-[0.98]">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted text-muted-foreground">
                        <Sun size={18} className="dark:hidden" />
                        <Moon size={18} className="hidden dark:block" />
                      </div>
                      <span className="text-base font-medium text-foreground">
                        {t("navigation.theme", "Theme")}
                      </span>
                    </div>
                    <div data-theme-toggle>
                      <ThemeToggle />
                    </div>
                  </button>
                </div>

                {/* Divider */}
                <div className="px-3">
                  <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                </div>

                {/* Account Section */}
                <div className="space-y-3 pt-2">
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-medieval font-semibold text-muted-foreground uppercase tracking-wide">
                      Account
                    </h3>
                  </div>
                  
                  <div className="space-y-3 px-2">
                    {session ? <>
                        <TouchOptimizedButton asChild variant="outline" size="touch" className="w-full text-base h-12 rounded-xl border-border/50 bg-card hover:bg-muted/50 font-medium">
                          <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                            <User size={18} className="mr-2" />
                            {t("navigation.dashboard")}
                          </Link>
                        </TouchOptimizedButton>
                        <TouchOptimizedButton onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }} variant="primary" size="touch" className="w-full text-base h-12 rounded-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-lg shadow-primary/25 font-medium">
                          <LogOut size={18} className="mr-2" />
                          {t("navigation.logout")}
                        </TouchOptimizedButton>
                      </> : <>
                        <TouchOptimizedButton asChild variant="outline" size="touch" className="w-full text-base h-12 rounded-xl border-border/50 bg-card hover:bg-muted/50 font-medium">
                          <Link to="/login" onClick={() => setIsOpen(false)}>
                            <User size={18} className="mr-2" />
                            {t("navigation.login")}
                          </Link>
                        </TouchOptimizedButton>
                        <TouchOptimizedButton asChild variant="primary" size="touch" className="w-full text-base h-12 rounded-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-lg shadow-primary/25 font-medium">
                          <Link to="/signup" onClick={() => setIsOpen(false)}>
                            <Crown size={18} className="mr-2" />
                            {t("navigation.signup")}
                          </Link>
                        </TouchOptimizedButton>
                      </>}
                  </div>
                </div>
              </div>
            </div>
          </SmoothTransition>
        </div>
      </nav>;
};
export default Navigation;